/**
 * Sessions, History: what ran in the page's range (ADR 0109). Starts per bucket by agent, the
 * ended sessions as a timeline per machine, then the list with search. The transcript stays on
 * its machine: a row opens the session drawer, which asks the machine for it.
 */
import { Search, SquareTerminal } from '@orgus/ui/icons';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { EmptyState, ErrorState, Input, ListRow, Panel, RowCell, RowList, RowTitle, SkeletonRows, TimelineChart, formatDateRange, formatDuration, formatMoney, formatRelative, plural } from '@orgus/ui';
import type { DateRange } from '@orgus/ui';
import { HistoryChart, groupLabel } from './HistoryChart.tsx';
import { useHistorySessions } from './data.ts';

const OUTCOME: Record<string, string> = { done: 'Finished', empty: 'No turns', gone: 'Stopped reporting' };

export function SessionHistory({ range }: { range: DateRange }) {
  const [q, setQ] = useState('');
  const [, setParams] = useSearchParams();
  const r = useHistorySessions(range, q.trim());
  const sessions = r.data?.sessions ?? [];
  const open = (id: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('session', id);
        return n;
      },
      { replace: false },
    );
  const label = formatDateRange(range);
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 xl:grid-cols-2">
        <HistoryChart range={range} title="Sessions started" metrics={['sessions.started']} groupBy="agent" noun="sessions started" />
        <HistoryChart range={range} title="Sessions ended, by outcome" metrics={['sessions.ended']} groupBy="outcome" noun="sessions ended" />
      </div>
      <TimelineChart
        title="When they ran"
        summary={`${plural(sessions.length, 'ended session')} across ${new Set(sessions.map((s) => s.machine)).size} machines, ${label}`}
        spans={sessions.map((s) => ({ id: s.id, lane: s.machine, start: s.startedAt, end: s.endedAt, label: s.title || 'Untitled session', group: s.agent, ...(s.asked ? { marks: s.asked } : {}) }))}
        from={range.from}
        to={range.to}
        groupLabel={groupLabel}
        onSelect={open}
        loading={r.loading}
        error={r.data ? undefined : (r.error ?? undefined)}
        onRetry={r.retry}
        partial={r.data?.unreadable.length ? `${plural(r.data.unreadable.length, 'day')} could not be read: ${r.data.unreadable.join(', ')}.` : r.data?.next ? 'The newest 100 sessions in range.' : undefined}
        emptyText={{ title: 'No sessions ended in this range', description: 'Pick a longer range.' }}
      />
      <Panel
        title="Ended sessions"
        sub={r.data ? plural(sessions.length, 'session') + (r.data.next ? ' or more' : '') : undefined}
        icon={<SquareTerminal />}
        flush
        actions={<Input size="sm" aria-label="Search ended sessions" placeholder="Search title, model, machine" icon={<Search />} value={q} onChange={(e) => setQ(e.target.value)} wrapperClassName="w-56" />}
      >
        {r.loading ? (
          <SkeletonRows rows={5} />
        ) : r.error && !r.data ? (
          <ErrorState error={r.error} onRetry={r.retry} />
        ) : !sessions.length ? (
          <EmptyState icon={<SquareTerminal />} title={q ? 'No ended session matches' : 'No sessions ended in this range'} description={q ? 'Try fewer words.' : `Nothing ended ${label === 'Today' ? 'today' : `from ${label}`}.`} />
        ) : (
          <RowList label="Ended sessions">
            {sessions.map((s) => (
              <ListRow
                key={s.id}
                onOpen={() => open(s.id)}
                trail={<RowCell numeric width="w-20">{formatRelative(s.endedAt)}</RowCell>}
                aria-label={s.title || 'Untitled session'}
              >
                <RowTitle title={s.title || 'Untitled session'} meta={`${s.project ?? 'No project'}, ${s.machine}`} />
                <RowCell hideBelow="md" width="w-28">
                  {OUTCOME[s.outcome] ?? s.outcome}
                </RowCell>
                <RowCell numeric hideBelow="lg" width="w-20">
                  {formatDuration(s.endedAt - s.startedAt)}
                </RowCell>
                <RowCell numeric hideBelow="md" width="w-20">
                  {formatMoney(s.costUsd)}
                </RowCell>
              </ListRow>
            ))}
          </RowList>
        )}
      </Panel>
    </div>
  );
}
